import React, { useState, useEffect } from 'react';
import type { ComplianceCheck, SalesReportSummary } from '../utils/types';
import type { ConsumerVerdict } from '../utils/audience';
import { getReportSummary } from '../utils/api';
import { T } from '../designTokens';
import type { SignedOutUnlock } from './PaywallOffer';
import EmailReportForm from './EmailReportForm';
import ReportTeaser from './teaser/ReportTeaser';
import { useFreeAuditCta } from './teaser/FreeAuditCta';
import { ownerUnlockCta } from './teaser/OwnerUnlockCta';
import { getScanIntentId, getScanIntentToken, trackLockedReportViewed } from '@privisy/ui-bridge';

interface LockedReportViewProps {
  reportId?: string | null;
  /** The scan behind the report; with an intent token, enables "Email me this report" (#1780). */
  scanId?: string | null;
  userId?: string | null;
  isSignedIn?: boolean;
  authEnabled?: boolean;
  balance?: number | null;
  creditsLoading?: boolean;
  onRunFullAudit?: (url: string) => Promise<void> | void;
  isLoading?: boolean;
  signInButton?: React.ReactNode;
  /** The parent scan ran GPC validation only; frame the CTA as an upgrade to the full scan. */
  gpcOnly?: boolean;
  /** Navigate back to the scanner — shown on the summary-load-failure state. */
  onBack?: () => void;
  /**
   * Buy-credits pack price in cents, from useCreditInfo. Deliberately without
   * a default: both values are env-configurable (CREDIT_PACK_PRICE_CENTS,
   * CREDITS_PER_PURCHASE), so a local default here would be a second home for
   * pricing that silently advertises stale numbers while Stripe charges the
   * current ones. Optional only because GET /api/credits may not have
   * answered yet, in which case there is no price to show (#970).
   */
  packPrice?: number;
  /** Credits granted per pack purchase, from useCreditInfo. See packPrice. */
  creditsPerPurchase?: number;
  /** Called after the report was unlocked in place (#1762), to reload it. */
  onUnlocked?: () => void;
  /** #1779: the signed-out claim holder's unlock path (see PaywallOffer). */
  signedOutUnlock?: SignedOutUnlock;
  /** #2046: frame the teaser for a /check visitor (see ReportTeaser). */
  consumer?: ConsumerVerdict;
  /** Rendered inside the teaser beside the unlock offer: the Shield offer (#2132). */
  alternateOffer?: React.ReactNode;
  /**
   * The CCPA checks of the locked ScanResult, for the owner's findings list
   * (names and severities; the mask has already stripped the proof). Never
   * shown to a viewer who does not own the report.
   */
  checks?: ComplianceCheck[];
}

/**
 * The intent token for `scanId` from the scan-intent cookies, or null. A
 * token whose cookie scan id names a different scan is not this report's.
 */
function intentTokenFor(scanId: string | null | undefined): string | null {
  if (!scanId) return null;
  const token = getScanIntentToken();
  if (!token) return null;
  const persistedId = getScanIntentId();
  return persistedId && persistedId !== scanId ? null : token;
}

export default function LockedReportView({
  reportId,
  scanId,
  userId,
  isSignedIn,
  authEnabled,
  balance,
  creditsLoading = false,
  onRunFullAudit,
  isLoading: isLoadingProp,
  signInButton,
  gpcOnly = false,
  onBack,
  packPrice,
  creditsPerPurchase,
  onUnlocked,
  signedOutUnlock,
  consumer,
  alternateOffer,
  checks,
}: LockedReportViewProps) {
  const [summary, setSummary] = useState<SalesReportSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const freeAuditCta = useFreeAuditCta(summary?.url ?? '');

  useEffect(() => {
    if (reportId) {
      trackLockedReportViewed(reportId);
    }
  }, [reportId]);

  useEffect(() => {
    if (!reportId) {
      setSummary(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    getReportSummary(reportId)
      .then(setSummary)
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Failed to load report summary';
        setError(message);
      })
      .finally(() => setIsLoading(false));
  }, [reportId]);

  const isReallyLoading = isLoadingProp !== undefined ? isLoadingProp : (isLoading || creditsLoading);

  if (isReallyLoading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: T.gray500 }}>
        Loading compliance summary...
      </div>
    );
  }

  if (error || !summary) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: T.gray500 }}>
        <p style={{ marginBottom: '16px', fontSize: '14px' }}>
          Unable to load compliance summary.
        </p>
        <button onClick={onBack} className="legacy-cta__button">
          Go to scanner
        </button>
      </div>
    );
  }

  // #1807: one teaser for every locked report; only the CTA slot depends on
  // who is looking. The unlock checkout is owner-only, so the owner and the
  // signed-out claim holder get the unlock, and anyone else who opens this
  // report by its ID gets their own free audit. A summary without
  // `viewerIsOwner` (an older API) counts as the owner, today's behaviour.
  const ownerView = summary.viewerIsOwner !== false || Boolean(signedOutUnlock);
  const cta = ownerView
    ? ownerUnlockCta({
      summary, reportId, userId, isSignedIn, authEnabled, balance, packPrice, creditsPerPurchase,
      onRunFullAudit, signInButton, gpcOnly, onUnlocked, signedOutUnlock,
      consumer: Boolean(consumer), findings: checks,
    })
    : freeAuditCta;
  const emailToken = isSignedIn ? null : intentTokenFor(scanId);

  const afterCta = (
    <>
      {gpcOnly && (
        <div
          className="legacy-cta__gpc-note"
          role="note"
          style={{ margin: '16px 0 0', padding: '12px 16px', borderRadius: 8,
                   backgroundColor: T.cobalt50, border: `1px solid ${T.cobalt200}`, color: T.cobalt700, fontSize: 13 }}
        >
          This scan checked Global Privacy Control handling only. The full audit also
          checks required disclosures, third-party trackers, and your privacy policy. Free
          full audits are limited per day, and a scan past that limit runs this check instead.
        </div>
      )}
      {/* #1780: optional, below the paywall, never for a signed-in visitor. */}
      {!isSignedIn && scanId && emailToken && (
        <EmailReportForm scanId={scanId} intentToken={emailToken} />
      )}
    </>
  );

  return (
    <div className="sales-report sales-report--embedded">
      {/* gpcOnly: the note below the CTA already names the single section
          that ran, so suppress the redundant "not tested" clause for the
          rest, but still surface a stage that errored outright, which that
          note says nothing about (#1168 review). */}
      <ReportTeaser
        reportId={summary.reportId}
        summary={summary}
        ownerView={ownerView}
        cta={cta}
        afterCta={afterCta}
        alternateOffer={alternateOffer}
        partialOnlyErrors={gpcOnly}
        consumer={consumer}
        findings={ownerView ? checks : undefined}
      />
    </div>
  );
}
